import { Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faPlus, faTimes, faSave, faTrash, faEdit, faChevronUp, faChevronDown, faUpload, faDownload } from '@fortawesome/free-solid-svg-icons';
import { AgGridAngular } from 'ag-grid-angular';
import { CellValueChangedEvent, ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { AgGridThemeService } from '../../../_services/ag-grid-theme.service';
import { AG_GRID_LOCALE_DE } from '../../../_helpers/aggrid.locale.de';
import { StagingConfigService, UnusedReserve, RedistributionCandidate } from '../../../_services/staging-config.service';
import { ArticleService, ProjectService } from '../../../_services';
import { ArticleTemplate, Priority } from '../../../_models';
import { StorePickerStore } from '../../../_models/store.interface';
import { StorePickerComponent } from '../../_shared/store-picker/store-picker.component';
import {
  CreateStagingConfigParams,
  StagingConfig,
  StagingStepDefinition,
  StagingStepItem,
  ExcelDataRow,
  StagingRecordListItem,
} from '../../../_models/staging-config.interface';
import * as XLSX from 'xlsx';

const STEP_ITEM_TYPES: { value: StagingStepItem['type']; label: string }[] = [
  { value: 'checklist', label: 'Checkliste' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'text_input', label: 'Texteingabe' },
  { value: 'number_input', label: 'Zahleneingabe' },
  { value: 'select', label: 'Auswahl (Dropdown)' },
  { value: 'image_capture', label: 'Foto aufnehmen' },
  { value: 'mac_print', label: 'MAC drucken' },
  { value: 'info', label: 'Info-Text (nur Anzeige)' },
];

@Component({
  selector: 'app-staging-config-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, FaIconComponent, AgGridAngular, StorePickerComponent],
  host: { class: 'flex flex-col h-full overflow-y-auto' },
  templateUrl: './staging-config-tab.component.html',
})
export class StagingConfigTabComponent implements OnInit {
  @Input() projectId!: number;

  private stagingService = inject(StagingConfigService);
  private articleService = inject(ArticleService);
  private projectService = inject(ProjectService);
  protected gridTheme = inject(AgGridThemeService).getTheme();
  protected gridLocale = AG_GRID_LOCALE_DE;

  // Icons
  protected readonly faPlus = faPlus;
  protected readonly faTimes = faTimes;
  protected readonly faSave = faSave;
  protected readonly faTrash = faTrash;
  protected readonly faEdit = faEdit;
  protected readonly faChevronUp = faChevronUp;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faUpload = faUpload;
  protected readonly faDownload = faDownload;
  protected readonly stepItemTypes = STEP_ITEM_TYPES;

  // State
  protected configs = this.stagingService.configs;
  protected isLoading = this.stagingService.isLoading;
  protected showForm = signal<boolean>(false);
  protected editingConfigId = signal<number | null>(null);

  // Formular-Felder
  protected formName = '';
  protected formDescription = '';
  protected formArticleTemplateId: number | null = null;
  protected formEnabled = true;
  protected formPackageSize: number | null = null;
  protected formSteps: StagingStepDefinition[] = [];
  protected formAuthorizeSerialNumbers = false;
  protected formStagingSlotCount: number = 10;
  protected formWeeklySchedule: { year: number; staging_kw: number; week_color: string; shipping_kw: number; installation_kw: number; staging_deadline: string }[] = [];

  // Ticket-Zusammenfassung (wöchentliches „Staging Volumen KW XX"-Ticket)
  protected formSummaryPriorityId: number | null = null;
  protected formSummaryStoreId: number | null = null;
  protected formSummaryStoreName = '';
  protected priorities = signal<Priority[]>([]);
  protected showStorePicker = signal<boolean>(false);

  // Template-Suche
  protected articleTemplateSearch = '';
  protected articleTemplates = signal<ArticleTemplate[]>([]);
  protected filteredTemplates = signal<ArticleTemplate[]>([]);
  protected showTemplateDropdown = signal<boolean>(false);
  protected selectedTemplateName = '';

  // Excel-Daten Ansicht
  protected excelUploadConfigId = signal<number | null>(null);
  protected excelViewConfigId = signal<number | null>(null);
  protected excelData = signal<ExcelDataRow[]>([]);
  protected excelAuthorize = signal<boolean>(false);
  protected excelColumns = signal<string[]>([]);
  protected editingRowIndex = signal<number | null>(null);
  protected editingRowData = signal<ExcelDataRow | null>(null);
  protected bulkAuthorizeInput = '';
  protected isExcelLoading = signal<boolean>(false);
  protected hideStaged = signal<boolean>(true);
  protected stagedSerialNumbers = signal<Set<string>>(new Set());

  // Excel-Tabelle als AG-Grid: rows enthalten den Original-Index als
  // __index-Property, damit onCellValueChanged die richtige Zeile updaten
  // kann (rowIndex aendert sich bei Filter/Sort). Die "Gestaged ausblenden"-
  // Toggle wirkt als External-Filter — Quick-Filter (Suche) plus AG-Grid-
  // Spalten-Filter laufen unabhaengig.
  protected excelGridRowData = computed<(ExcelDataRow & { __index: number })[]>(() =>
    this.excelData().map((row, i) => ({ ...row, __index: i }))
  );
  protected excelGridQuickFilter = signal<string>('');
  protected excelColumnDefs = computed<ColDef[]>(() => {
    const cols: ColDef[] = this.excelColumns().map((col) => ({
      headerName: col,
      field: col,
      editable: true,
      filter: 'agTextColumnFilter',
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 120,
    }));

    if (this.excelAuthorize()) {
      cols.push({
        headerName: 'Freigabe',
        field: 'authorized',
        width: 110,
        sortable: true,
        filter: 'agSetColumnFilter',
        cellRenderer: (p: { data?: ExcelDataRow & { __index: number } }) =>
          `<input type="checkbox" class="bm-checkbox bm-checkbox-xs" ${p.data?.['authorized'] ? 'checked' : ''} />`,
        onCellClicked: (event) => {
          if (event.data) this.toggleRowAuthorized(event.data.__index);
        },
        cellStyle: { textAlign: 'center', cursor: 'pointer' },
      });
    }

    return cols;
  });
  protected excelDefaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: true,
  };
  private excelGridApi: GridApi | null = null;

  // Records-Tabelle als AG-Grid (read-only, Bericht-Button als Cell-Renderer).
  protected recordsGridQuickFilter = signal<string>('');
  protected recordsColumnDefs = computed<ColDef<StagingRecordListItem>[]>(() => [
    {
      headerName: 'KW',
      field: 'staging_kw',
      width: 110,
      valueGetter: (p) => p.data?.staging_kw ? `KW${p.data.staging_kw}/${p.data.staging_year ?? ''}` : '-',
      filter: 'agTextColumnFilter',
    },
    { headerName: 'SN', field: 'serial_number', flex: 1, minWidth: 140, filter: 'agTextColumnFilter' },
    { headerName: 'MAC', field: 'mac_address', flex: 1, minWidth: 160, filter: 'agTextColumnFilter' },
    {
      headerName: 'Status',
      width: 140,
      filter: 'agTextColumnFilter',
      valueGetter: (p) => this.recordStatusLabel(p.data),
      cellRenderer: (p: { data?: StagingRecordListItem }) => {
        const label = this.recordStatusLabel(p.data);
        const cls = this.recordStatusBadgeClass(p.data);
        return `<span class="bm-badge ${cls} bm-badge-xs">${label}</span>`;
      },
    },
    {
      headerName: 'Abgeschlossen',
      field: 'completed_at',
      width: 160,
      filter: 'agDateColumnFilter',
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '-',
    },
    { headerName: 'Von', field: 'completed_by_name', flex: 1, minWidth: 140, filter: 'agTextColumnFilter' },
    {
      headerName: '',
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: (p: { data?: StagingRecordListItem }) =>
        p.data?.has_report ? '<button class="bm-btn bm-btn-xs bm-btn-ghost">Bericht</button>' : '',
      onCellClicked: (event) => {
        const cfgId = this.recordsViewConfigId();
        if (cfgId && event.data?.has_report) this.openReport(cfgId, event.data.id);
      },
      cellStyle: { textAlign: 'center', cursor: 'pointer' },
    },
  ]);
  protected recordsDefaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: true,
  };
  private recordsGridApi: GridApi | null = null;

  protected onExcelGridReady = (event: GridReadyEvent) => {
    this.excelGridApi = event.api;
    this.applyExcelExternalFilter();
  };

  protected onRecordsGridReady = (event: GridReadyEvent) => {
    this.recordsGridApi = event.api;
  };

  // Wird aufgerufen, wenn der User eine Zelle direkt im Grid editiert.
  // Sofortiges Persistieren via stagingService.updateExcelRow — das alte
  // Inline-Edit (startEditRow / saveEditRow) wird damit obsolet.
  protected async onExcelCellValueChanged(event: CellValueChangedEvent): Promise<void> {
    const configId = this.excelViewConfigId();
    if (!configId || !event.data) return;
    const idx: number = event.data.__index;
    const rowForBackend: ExcelDataRow = { ...event.data };
    delete (rowForBackend as Record<string, unknown>)['__index'];
    const success = await this.stagingService.updateExcelRow(configId, idx, rowForBackend);
    if (success) {
      this.excelData.update((rows) => rows.map((r, i) => (i === idx ? rowForBackend : r)));
    } else {
      this.excelGridApi?.refreshCells({ force: true });
    }
  }

  // External Filter: "Gestaged ausblenden". Wird ueber AG-Grid's
  // doesExternalFilterPass-Hook angewendet und reagiert auf den hideStaged-
  // Toggle (siehe applyExcelExternalFilter).
  protected isExcelExternalFilterPresent = (): boolean => {
    if (!this.hideStaged()) return false;
    return this.stagedSerialNumbers().size > 0;
  };

  protected doesExcelExternalFilterPass = (node: { data?: ExcelDataRow }): boolean => {
    if (!node.data) return true;
    const snCol = this.excelColumns().find((c) => c.toLowerCase().includes('sn') || c.toLowerCase().includes('serial'));
    if (!snCol) return true;
    return !this.stagedSerialNumbers().has(String(node.data[snCol] || ''));
  };

  protected toggleHideStaged(): void {
    this.hideStaged.set(!this.hideStaged());
    this.applyExcelExternalFilter();
  }

  private applyExcelExternalFilter(): void {
    this.excelGridApi?.onFilterChanged();
  }

  protected onExcelQuickFilterChange(value: string): void {
    this.excelGridQuickFilter.set(value);
    this.excelGridApi?.setGridOption('quickFilterText', value);
  }

  protected onRecordsQuickFilterChange(value: string): void {
    this.recordsGridQuickFilter.set(value);
    this.recordsGridApi?.setGridOption('quickFilterText', value);
  }

  private recordStatusLabel(rec: StagingRecordListItem | undefined): string {
    if (!rec) return '';
    if (rec.parked) return 'Zurückgestellt';
    if (rec.shipped_at) return 'Versandt';
    if (rec.status === 'completed') return 'Gestaged';
    return 'Offen';
  }

  private recordStatusBadgeClass(rec: StagingRecordListItem | undefined): string {
    if (!rec) return 'bm-badge-neutral';
    if (rec.parked) return 'bm-badge-error';
    if (rec.shipped_at) return 'bm-badge-info';
    if (rec.status === 'completed') return 'bm-badge-success';
    return 'bm-badge-warning';
  }

  // Export-Dialog
  protected showExportDialog = signal<boolean>(false);
  protected exportConfigId = signal<number | null>(null);
  protected exportKwFrom = 1;
  protected exportKwTo = 52;
  protected exportYear = new Date().getFullYear();
  protected exportIsLoading = signal<boolean>(false);
  protected exportColumns: { key: string; label: string; enabled: boolean }[] = [];

  // Step-Editor Views
  protected stepsJsonMode = signal<boolean>(false);
  protected stepsJsonText = '';
  protected stepsJsonError = signal<string>('');

  // Ungenutzte Reserve
  protected reservesViewConfigId = signal<number | null>(null);
  protected reservesList = signal<UnusedReserve[]>([]);
  protected isReservesLoading = signal<boolean>(false);

  // Records-Ansicht
  protected recordsViewConfigId = signal<number | null>(null);
  protected recordsList = signal<StagingRecordListItem[]>([]);
  protected isRecordsLoading = signal<boolean>(false);


  async ngOnInit() {
    await this.stagingService.loadConfigs(this.projectId);
    this.loadArticleTemplates();
    this.projectService.getPriorities(this.projectId).subscribe({
      next: (data) => this.priorities.set(data),
      error: (err) => console.error('Fehler beim Laden der Prioritäten:', err),
    });
  }

  // --- Config CRUD ---

  openCreateForm() {
    this.resetForm();
    this.editingConfigId.set(null);
    this.showForm.set(true);
  }

  openEditForm(config: StagingConfig) {
    this.formName = config.name;
    this.formDescription = config.description || '';
    this.formArticleTemplateId = config.article_template_id || null;
    this.formEnabled = config.enabled;
    this.formPackageSize = config.package_size || null;
    this.formSteps = JSON.parse(JSON.stringify(config.steps || []));
    this.formAuthorizeSerialNumbers = config.authorize_serial_numbers || false;
    this.formStagingSlotCount = config.staging_slot_count || 10;
    this.formWeeklySchedule = JSON.parse(JSON.stringify(config.weekly_schedule || []));
    this.formSummaryPriorityId = config.summary_priority_id ?? null;
    this.formSummaryStoreId = config.summary_store_id ?? null;
    this.formSummaryStoreName = config.summary_store_name || '';
    this.selectedTemplateName = config.article_template_description || '';
    this.articleTemplateSearch = config.article_template_description || '';
    this.editingConfigId.set(config.id);
    this.showForm.set(true);
  }

  cancelForm() {
    this.resetForm();
    this.showForm.set(false);
    this.editingConfigId.set(null);
  }

  async saveForm() {
    if (!this.formName.trim()) return;

    // Falls JSON-Modus aktiv, zuerst parsen
    if (this.stepsJsonMode()) {
      if (!this.applyStepsJson()) return;
    }

    const params: CreateStagingConfigParams & { enabled?: boolean } = {
      name: this.formName.trim(),
      description: this.formDescription.trim() || undefined,
      article_template_id: this.formArticleTemplateId ?? undefined,
      enabled: this.formEnabled,
      package_size: this.formPackageSize ?? undefined,
      authorize_serial_numbers: this.formAuthorizeSerialNumbers,
      steps: this.formSteps,
      staging_slot_count: this.formStagingSlotCount,
      weekly_schedule: this.formWeeklySchedule.length > 0 ? this.formWeeklySchedule : undefined,
      // null bewusst mitsenden, damit Leeren der Felder funktioniert
      summary_priority_id: this.formSummaryPriorityId,
      summary_store_id: this.formSummaryStoreId,
    };

    const editId = this.editingConfigId();
    if (editId) {
      await this.stagingService.updateConfig(editId, params);
    } else {
      await this.stagingService.createConfig(this.projectId, params);
    }

    this.cancelForm();
  }

  async deleteConfig(config: StagingConfig) {
    if (!confirm(`Config "${config.name}" wirklich löschen?`)) return;
    await this.stagingService.deleteConfig(config.id);
  }

  async toggleEnabled(config: StagingConfig) {
    await this.stagingService.updateConfig(config.id, { enabled: !config.enabled });
  }

  async toggleStepEnabled(config: StagingConfig, stepIndex: number) {
    const steps = JSON.parse(JSON.stringify(config.steps || []));
    if (steps[stepIndex]) {
      steps[stepIndex].enabled = !steps[stepIndex].enabled;
      await this.stagingService.updateConfig(config.id, { steps });
    }
  }

  // --- Template-Suche ---

  loadArticleTemplates() {
    this.articleService.loadArticleTemplates().subscribe({
      next: (templates) => this.articleTemplates.set(templates),
    });
  }

  onTemplateSearchInput() {
    const term = this.articleTemplateSearch.toLowerCase();
    if (term.length < 2) {
      this.filteredTemplates.set([]);
      this.showTemplateDropdown.set(false);
      return;
    }
    const filtered = this.articleTemplates().filter(t =>
      t.description?.toLowerCase().includes(term)
    );
    this.filteredTemplates.set(filtered.slice(0, 20));
    this.showTemplateDropdown.set(true);
  }

  selectTemplate(template: ArticleTemplate) {
    this.formArticleTemplateId = template.id;
    this.articleTemplateSearch = template.description;
    this.selectedTemplateName = template.description;
    this.showTemplateDropdown.set(false);
  }

  clearTemplate() {
    this.formArticleTemplateId = null;
    this.articleTemplateSearch = '';
    this.selectedTemplateName = '';
  }

  // --- Step-Editor ---

  addStep() {
    const nextNumber = this.formSteps.length > 0
      ? Math.max(...this.formSteps.map(s => s.number)) + 1
      : 1;
    this.formSteps.push({
      number: nextNumber,
      title: '',
      enabled: true,
      items: [],
    });
  }

  removeStep(index: number) {
    this.formSteps.splice(index, 1);
  }

  moveStep(index: number, direction: 'up' | 'down') {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= this.formSteps.length) return;
    const temp = this.formSteps[index];
    this.formSteps[index] = this.formSteps[newIndex];
    this.formSteps[newIndex] = temp;
    // Nummern aktualisieren
    this.formSteps.forEach((s, i) => s.number = i + 1);
  }

  addStepItem(stepIndex: number) {
    this.formSteps[stepIndex].items.push({
      type: 'checkbox',
      label: '',
    });
  }

  removeStepItem(stepIndex: number, itemIndex: number) {
    this.formSteps[stepIndex].items.splice(itemIndex, 1);
  }

  onStepItemTypeChange(item: StagingStepItem) {
    if (item.type === 'pdf_generate' && !item.pdf_config) {
      item.pdf_config = { width_mm: 50, content_field: '' };
    }
    if (item.type === 'select' && !item.options) {
      item.options = [];
    }
    if (item.type === 'mac_print') {
      item.label = 'MAC Drucken';
    }
    if (item.type === 'checklist') {
      if (!item.checklist_items) item.checklist_items = [];
      if (item.notify_on_incomplete === undefined) item.notify_on_incomplete = false;
      if (!item.notify_email) item.notify_email = 'helpdesk@bentomax.de';
    }
  }

  // --- Checklist-Item Verwaltung ---

  addChecklistItem(item: StagingStepItem) {
    if (!item.checklist_items) item.checklist_items = [];
    item.checklist_items.push('');
  }

  removeChecklistItem(item: StagingStepItem, index: number) {
    item.checklist_items?.splice(index, 1);
  }

  trackByIndex(index: number) {
    return index;
  }

  // --- Excel-Upload ---

  onExcelFileSelected(event: Event, configId: number) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const data = new Uint8Array(reader.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: ExcelDataRow[] = XLSX.utils.sheet_to_json(sheet);

      if (rows.length > 0) {
        await this.stagingService.uploadExcelData(configId, rows);
      }

      // Input zurücksetzen
      input.value = '';
    };
    reader.readAsArrayBuffer(file);
  }

  // --- Steps JSON View ---

  toggleStepsJsonMode() {
    if (this.stepsJsonMode()) {
      // JSON → Editor: versuchen zu parsen
      this.applyStepsJson();
    } else {
      // Editor → JSON: aktuellen State serialisieren
      this.stepsJsonText = JSON.stringify(this.formSteps, null, 2);
      this.stepsJsonError.set('');
    }
    this.stepsJsonMode.update(v => !v);
  }

  applyStepsJson() {
    try {
      const parsed = JSON.parse(this.stepsJsonText);
      if (!Array.isArray(parsed)) {
        this.stepsJsonError.set('JSON muss ein Array sein.');
        return false;
      }
      // Basis-Validierung: jeder Step braucht number, title, items
      for (const step of parsed) {
        if (typeof step.number !== 'number' || typeof step.title !== 'string' || !Array.isArray(step.items)) {
          this.stepsJsonError.set('Jeder Schritt braucht number (Zahl), title (String) und items (Array).');
          return false;
        }
      }
      this.formSteps = parsed;
      this.stepsJsonError.set('');
      return true;
    } catch (e) {
      this.stepsJsonError.set('Ungültiges JSON: ' + (e as Error).message);
      return false;
    }
  }

  // --- Records-Ansicht ---

  async toggleRecordsView(configId: number) {
    if (this.recordsViewConfigId() === configId) {
      this.recordsViewConfigId.set(null);
      this.recordsList.set([]);
      return;
    }
    this.isRecordsLoading.set(true);
    const records = await this.stagingService.loadRecords(configId);
    this.isRecordsLoading.set(false);
    this.recordsList.set(records);
    this.recordsViewConfigId.set(configId);
  }

  openReport(configId: number, recordId: number) {
    const url = this.stagingService.getReportUrl(configId, recordId);
    window.open(url, '_blank');
  }

  // --- Excel-Daten Ansicht ---

  async toggleExcelView(configId: number) {
    if (this.excelViewConfigId() === configId) {
      this.excelViewConfigId.set(null);
      this.excelData.set([]);
      return;
    }
    this.isExcelLoading.set(true);
    const [result, records] = await Promise.all([
      this.stagingService.loadExcelData(configId),
      this.stagingService.loadRecords(configId),
    ]);
    this.isExcelLoading.set(false);

    // SNs der bestehenden Records sammeln
    const sns = new Set<string>(records.filter(r => r.serial_number).map(r => r.serial_number!));
    this.stagedSerialNumbers.set(sns);

    if (result) {
      this.excelData.set(result.rows);
      this.excelAuthorize.set(result.authorize);
      // Spalten aus erster Zeile ableiten (ohne 'authorized')
      if (result.rows.length > 0) {
        this.excelColumns.set(Object.keys(result.rows[0]).filter(k => k !== 'authorized'));
      }
      this.excelViewConfigId.set(configId);
    }
  }

  startEditRow(index: number) {
    const row = this.excelData()[index];
    this.editingRowIndex.set(index);
    this.editingRowData.set({ ...row });
  }

  cancelEditRow() {
    this.editingRowIndex.set(null);
    this.editingRowData.set(null);
  }

  async saveEditRow() {
    const index = this.editingRowIndex();
    const data = this.editingRowData();
    const configId = this.excelViewConfigId();
    if (index === null || !data || !configId) return;

    const success = await this.stagingService.updateExcelRow(configId, index, data);
    if (success) {
      this.excelData.update(rows => rows.map((r, i) => i === index ? data : r));
      this.cancelEditRow();
    }
  }

  async authorizeAll() {
    const configId = this.excelViewConfigId();
    if (!configId) return;

    const snCol = this.excelColumns().find(c => c.toLowerCase().includes('sn') || c.toLowerCase().includes('serial'));
    if (!snCol) return;

    const sns = this.excelData()
      .filter(row => !row['authorized'] && row[snCol])
      .map(row => String(row[snCol]));

    if (sns.length === 0) return;
    if (!confirm(`Alle ${sns.length} nicht freigegebenen Seriennummern freigeben?`)) return;

    const count = await this.stagingService.authorizeBulk(configId, sns);
    if (count > 0) {
      await this.toggleExcelView(configId);
      await this.toggleExcelView(configId);
    }
  }

  async submitBulkAuthorize() {
    const configId = this.excelViewConfigId();
    if (!configId || !this.bulkAuthorizeInput.trim()) return;

    const sns = this.bulkAuthorizeInput
      .split(';')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    const count = await this.stagingService.authorizeBulk(configId, sns);
    if (count > 0) {
      // Daten neu laden
      this.bulkAuthorizeInput = '';
      await this.toggleExcelView(configId); // Schließen
      await this.toggleExcelView(configId); // Neu öffnen
    }
  }

  toggleRowAuthorized(index: number) {
    const rows = this.excelData();
    const row = { ...rows[index] };
    row['authorized'] = !row['authorized'];
    const configId = this.excelViewConfigId();
    if (!configId) return;
    this.stagingService.updateExcelRow(configId, index, row).then(success => {
      if (success) {
        this.excelData.update(r => r.map((item, i) => i === index ? row : item));
      }
    });
  }

  // --- Ungenutzte Reserve ---

  async toggleReservesView(configId: number) {
    if (this.reservesViewConfigId() === configId) {
      this.reservesViewConfigId.set(null);
      this.reservesList.set([]);
      return;
    }
    this.isReservesLoading.set(true);
    const reserves = await this.stagingService.loadUnusedReserves(configId);
    this.isReservesLoading.set(false);
    this.reservesList.set(reserves);
    this.reservesViewConfigId.set(configId);
  }

  // --- Umverteilung ---

  protected redistributingReserve = signal<UnusedReserve | null>(null);
  protected redistributeTargetKw = signal<RedistributionCandidate | null>(null);
  protected redistributeCount = 1;
  protected isRedistributing = signal<boolean>(false);

  openRedistributeDialog(reserve: UnusedReserve) {
    this.redistributingReserve.set(reserve);
    this.redistributeTargetKw.set(null);
    this.redistributeCount = Math.min(reserve.unused, reserve.candidates[0]?.remaining || 1);
  }

  closeRedistributeDialog() {
    this.redistributingReserve.set(null);
  }

  selectRedistributeTarget(candidate: RedistributionCandidate) {
    this.redistributeTargetKw.set(candidate);
    const reserve = this.redistributingReserve();
    if (reserve) {
      this.redistributeCount = Math.min(reserve.unused, candidate.remaining);
    }
  }

  async performRedistribute() {
    const reserve = this.redistributingReserve();
    const target = this.redistributeTargetKw();
    if (!reserve || !target) return;

    const configId = this.reservesViewConfigId();
    if (!configId) return;

    const count = Math.min(this.redistributeCount, reserve.unused, target.remaining);
    const recordIds = reserve.record_ids.slice(0, count);

    if (!confirm(`${count} Artikel von KW${reserve.staging_kw} nach KW${target.staging_kw}/${target.year} umverteilen?`)) return;

    this.isRedistributing.set(true);
    const result = await this.stagingService.redistribute(configId, recordIds, target.staging_kw, target.year);
    this.isRedistributing.set(false);

    if (result.success) {
      this.closeRedistributeDialog();
      // Reserven neu laden
      const reserves = await this.stagingService.loadUnusedReserves(configId);
      this.reservesList.set(reserves);
    }
  }

  // --- Excel-Export ---

  openExportDialog(configId: number) {
    this.exportConfigId.set(configId);
    this.exportKwFrom = 1;
    this.exportKwTo = 52;
    this.exportYear = new Date().getFullYear();
    this.exportColumns = [
      { key: 'staging_kw', label: 'Staging KW', enabled: true },
      { key: 'serial_number', label: 'Seriennummer', enabled: true },
      { key: 'mac_address', label: 'MAC-Adresse', enabled: true },
      { key: 'status', label: 'Status', enabled: true },
      { key: 'completed_at', label: 'Abgeschlossen am', enabled: true },
      { key: 'completed_by_name', label: 'Abgeschlossen von', enabled: true },
      { key: 'shipped_at', label: 'Versandt am', enabled: true },
      { key: 'shipped_to_user_name', label: 'Versandt an (Techniker)', enabled: true },
    ];
    this.showExportDialog.set(true);
  }

  closeExportDialog() {
    this.showExportDialog.set(false);
    this.exportConfigId.set(null);
  }

  async performExport() {
    const configId = this.exportConfigId();
    if (!configId) return;

    this.exportIsLoading.set(true);
    const allRecords = await this.stagingService.loadRecords(configId);
    this.exportIsLoading.set(false);

    // Nach KW-Range filtern und nach Staging KW aufsteigend sortieren
    const filtered = allRecords
      .filter(r => {
        if (!r.staging_kw || !r.staging_year) return false;
        if (r.staging_year !== this.exportYear) return false;
        return r.staging_kw >= this.exportKwFrom && r.staging_kw <= this.exportKwTo;
      })
      .sort((a, b) => (a.staging_kw || 0) - (b.staging_kw || 0));

    if (filtered.length === 0) {
      alert('Keine Records im gewählten KW-Bereich gefunden.');
      return;
    }

    // Aktive Spalten ermitteln
    const activeCols = this.exportColumns.filter(c => c.enabled);

    // Daten für Excel aufbereiten
    const rows = filtered.map(rec => {
      const row: Record<string, string | number> = {};
      for (const col of activeCols) {
        switch (col.key) {
          case 'staging_kw':
            row[col.label] = rec.staging_kw ? `KW${rec.staging_kw}/${rec.staging_year}` : '';
            break;
          case 'status':
            row[col.label] = rec.parked ? 'Zurückgestellt' : rec.shipped_at ? 'Versandt' : rec.status === 'completed' ? 'Gestaged' : 'Offen';
            break;
          case 'shipped_at':
            row[col.label] = rec.shipped_at ? new Date(rec.shipped_at).toLocaleString('de-DE') : '';
            break;
          case 'completed_at':
            row[col.label] = rec.completed_at ? new Date(rec.completed_at).toLocaleString('de-DE') : '';
            break;
          default:
            row[col.label] = (rec as any)[col.key] ?? '';
        }
      }
      return row;
    });

    // Excel generieren mit breiten Spalten
    const config = this.configs().find(c => c.id === configId);
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = activeCols.map(() => ({ wch: 28 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Staging Records');
    const filename = `staging-${config?.name || configId}-KW${this.exportKwFrom}-${this.exportKwTo}-${this.exportYear}.xlsx`;
    XLSX.writeFile(wb, filename);

    this.closeExportDialog();
  }

  // --- Hilfsmethoden ---

  private resetForm() {
    this.formName = '';
    this.formDescription = '';
    this.formArticleTemplateId = null;
    this.formEnabled = true;
    this.formPackageSize = null;
    this.formAuthorizeSerialNumbers = false;
    this.formSteps = [];
    this.formStagingSlotCount = 10;
    this.formWeeklySchedule = [];
    this.formSummaryPriorityId = null;
    this.formSummaryStoreId = null;
    this.formSummaryStoreName = '';
    this.showStorePicker.set(false);
    this.articleTemplateSearch = '';
    this.selectedTemplateName = '';
    this.showTemplateDropdown.set(false);
  }

  // --- Ticket-Zusammenfassung (Filial-Picker) ---

  openStorePicker() {
    this.showStorePicker.set(true);
  }

  onSummaryStoreSelected(store: StorePickerStore) {
    this.formSummaryStoreId = store.id;
    this.formSummaryStoreName = store.name;
    this.showStorePicker.set(false);
  }

  clearSummaryStore() {
    this.formSummaryStoreId = null;
    this.formSummaryStoreName = '';
  }

  // --- Weekly Schedule Verwaltung ---

  addWeeklyScheduleEntry() {
    this.formWeeklySchedule = [...this.formWeeklySchedule, {
      year: new Date().getFullYear(),
      staging_kw: 0,
      week_color: '',
      shipping_kw: 0,
      installation_kw: 0,
      staging_deadline: '',
    }];
  }

  removeWeeklyScheduleEntry(index: number) {
    this.formWeeklySchedule = this.formWeeklySchedule.filter((_, i) => i !== index);
  }

  joinOptions(options?: string[]): string {
    return (options || []).join(', ');
  }

  parseOptions(value: string): string[] {
    return value.split(',').map(s => s.trim()).filter(s => s.length > 0);
  }

}
